import { SchemaFieldTypeExtended } from "config/config";

/**
 * Advanced-search filter support for custom-object ATTRIBUTE values (`value.<attr>`).
 *
 * The backend already accepts `filter_` clauses on `value.<attr>` paths for custom
 * objects (operator-task-co-attribute-search-filter). This module turns an object
 * template's attribute schema (the schema-form pushed over the websocket as
 * `dataStore.objectTemplateSchema[].schema`) into synthetic filter-builder fields so
 * the FE can expose those attributes in the advanced-search field dropdown, with a
 * datatype-correct operator + value control.
 *
 * A single attribute becomes a field whose `name` is `value.<attr>` — a plain (non
 * foreign-key, non `:`-nested) field, so it flows straight through the existing filter
 * pipeline: getMatchingSchema() finds it by name, getOperatorList() derives operators
 * from its `type`/`schema`, ValueField renders the value control from `schema`/`type`,
 * and makeQueryString() emits `{ field: "value.<attr>", op, value }` verbatim — exactly
 * the backend contract.
 *
 * This mapping is intentionally pure (no React) so it can be unit-tested without a
 * browser. See objectTemplateFilterFields.test.ts.
 */

/** Prefix marking a synthetic custom-object attribute filter field. */
export const VALUE_ATTR_PREFIX = "value.";

/** A single schema-form element/property of an object template's `schema.properties`. */
type SchemaFormProperty = {
    element?: string;
    label?: string;
    type?: string;
    format?: string;
    enum?: unknown[];
    default?: unknown;
    anyOf?: { type?: string; format?: string; enum?: unknown[] }[];
    [key: string]: unknown;
};

/** The `.schema` of an objectTemplateSchema entry (a `form-object` schema-form). */
export type ObjectTemplateSchema = {
    properties?: Record<string, SchemaFormProperty>;
    hidden?: string[];
} | null | undefined;

/**
 * A property is nullable if its schema-form element is one of the `form-optional-*`
 * variants, or if its `anyOf` explicitly admits `null`.
 */
const isNullableProperty = (property: SchemaFormProperty): boolean => {
    if (typeof property.element === "string" && property.element.startsWith("form-optional-")) {
        return true;
    }
    return Array.isArray(property.anyOf) && property.anyOf.some((s) => s?.type === "null");
};

/** Collect the non-null `anyOf` branches (the meaningful value schema of an optional field). */
const nonNullBranches = (property: SchemaFormProperty) =>
    Array.isArray(property.anyOf) ? property.anyOf.filter((s) => s?.type !== "null") : [];

/** Enum values declared either directly or inside an `anyOf` branch. */
const enumValues = (property: SchemaFormProperty): unknown[] | undefined => {
    if (Array.isArray(property.enum)) {
        return property.enum;
    }
    const branchWithEnum = nonNullBranches(property).find((s) => Array.isArray(s?.enum));
    return branchWithEnum?.enum;
};

/** date-time format declared either directly or inside an `anyOf` branch. */
const hasDateTimeFormat = (property: SchemaFormProperty): boolean => {
    if (property.format === "date-time") {
        return true;
    }
    return nonNullBranches(property).some((s) => s?.format === "date-time");
};

/** The JSON-schema `type` of the first non-null branch (or the property's own `type`). */
const branchType = (property: SchemaFormProperty): string | undefined => {
    if (typeof property.type === "string") {
        return property.type;
    }
    return nonNullBranches(property).find((s) => typeof s?.type === "string")?.type;
};

/**
 * Map a schema-form property to a filter-builder field `type` (the union used by
 * getOperatorList / ValueField). Datetime is detected before string because the
 * underlying JSON type of a datetime is still "string".
 */
const filterFieldType = (property: SchemaFormProperty): SchemaFieldTypeExtended["type"] => {
    const element = typeof property.element === "string" ? property.element : "";
    const jsonType = branchType(property);

    if (element.includes("boolean") || jsonType === "boolean") {
        return "bool";
    }
    if (element.includes("datetime") || hasDateTimeFormat(property)) {
        return "datetime";
    }
    if (
        element.includes("int") ||
        element.includes("number") ||
        jsonType === "integer" ||
        jsonType === "number"
    ) {
        return "int";
    }
    if (jsonType === "array") {
        return "list";
    }
    if (jsonType === "object") {
        return "dict";
    }
    // Everything else (string, markdown, text, autocomplete, enum, …) filters as text.
    return "str";
};

/**
 * A property is a record / object-template reference when its schema-form element is
 * an autocomplete lookup control (`form-autocomplete` / `form-optional-autocomplete`,
 * carrying `record_type` + `store: "record_id"`). Such a RECORD_REFERENCE /
 * OBJECT_TEMPLATE_REFERENCE attribute maps to a `postgresql.UUID` column on the
 * backend, so substring operators (`like`/`notlike` → `ILIKE`) are invalid against it
 * (Postgres has no `uuid ~~* uuid` operator — a raw `LIKE` filter 500s).
 */
const isReferenceProperty = (property: SchemaFormProperty): boolean =>
    typeof property.element === "string" && property.element.includes("autocomplete");

/** Build the `schema` sub-object consumed by getOperatorList / ValueField. */
const buildFieldSchema = (property: SchemaFormProperty): Record<string, unknown> => {
    const schema: Record<string, unknown> = {};
    const values = enumValues(property);
    if (values) {
        schema.enum = values;
    }
    if (hasDateTimeFormat(property)) {
        schema.format = "date-time";
    }
    if (isReferenceProperty(property)) {
        // uuid-backed reference: mark format "uuid" so getOperatorList excludes the
        // substring operators (like/notlike) that would otherwise 500 the backend —
        // matching the backend defence-in-depth guard in select_mixin._apply_filter_operator.
        schema.format = "uuid";
        // Preserve the referenced type so a value control can later render a record
        // picker (operator-task-advanced-search-…-uuid-value-picker, defect 2).
        if (typeof property.record_type === "string") {
            schema.record_type = property.record_type;
        }
        if (typeof property.object_template_id === "string") {
            schema.object_template_id = property.object_template_id;
        }
    }
    return schema;
};

/**
 * Turn one object-template attribute (property) into a synthetic filter field.
 * Exported for testing.
 */
export const objectTemplateAttributeToField = (
    attr: string,
    property: SchemaFormProperty
): SchemaFieldTypeExtended => ({
    name: `${VALUE_ATTR_PREFIX}${attr}`,
    label: typeof property.label === "string" ? property.label : attr,
    type: filterFieldType(property),
    schema: buildFieldSchema(property),
    // Attribute filters are plain value paths — never platform foreign keys.
    foreign_keys: [],
    referenced_by_foreign_keys: [],
    is_reference_column: false,
    nullable: isNullableProperty(property),
    reading: "OPTIONAL",
    creating: "OPTIONAL",
    can_duplicate: false,
    default: property.default,
    isExtended: false,
});

/**
 * Turn an object template's schema into the list of synthetic `value.<attr>` filter
 * fields. Returns [] when the schema has no properties. Hidden attributes are still
 * included — hiding governs column display, not filterability.
 */
export const getObjectTemplateAttributeFields = (
    objectTemplateSchema: ObjectTemplateSchema
): SchemaFieldTypeExtended[] => {
    const properties = objectTemplateSchema?.properties;
    if (!properties || typeof properties !== "object") {
        return [];
    }
    return Object.entries(properties).map(([attr, property]) =>
        objectTemplateAttributeToField(attr, property as SchemaFormProperty)
    );
};
